// src/components/student/StudentVideosPage.js
//
// My Videos page — matches sas_v3_my_videos mockup with warm light theme.
// Shows: XP strip, filter row, video card grid, upload CTA.
// Wired to real Firestore video data.

import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import { db } from '../../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { getStudentVideoState } from '../../utils/videoWorkflow';
import { isTenantlessLegacyVideo } from '../../utils/studentDataAccess';
import { buildMySkillsPath } from '../../utils/mySkillsPath';
import { useStudentContext } from '../../hooks/useStudentContext';
import { useStudentModernVideos } from '../../hooks/useStudentModernData';
import StudentNav from '../shared/StudentNav';
import StudentOrgBar from './StudentOrgBar';
import MotusIcon from '../shared/MotusIcon';

const C = {
  bg:'#FAFAF8', card:'#FFFFFF', card2:'#F5F4F0', card3:'#ECEAE4',
  border:'rgba(0,0,0,0.06)', borderH:'rgba(0,0,0,0.12)',
  blue:'#2563EB', blueDim:'rgba(37,99,235,0.08)',
  green:'#22C55E', greenDim:'rgba(34,197,94,0.08)',
  orange:'#F05A1F', orangeDim:'rgba(240,90,31,0.08)',
  purple:'#9333EA', gold:'#D97706', goldDim:'rgba(217,119,6,0.08)',
  red:'#DC2626',
  t1:'#1C1B18', t2:'#6B6B63', t3:'#A3A299',
};
const font = "'DM Sans',sans-serif";
const fontD = "'Bebas Neue',sans-serif";

function StudentVideosPage() {
  const navigate = useNavigate();
  const { currentUser, userData } = useAuth();
  const studentId = currentUser?.uid || '';

  // Pass 85.1A.2 — the CANONICAL ACCESS LANE decides data mode. MODERN → org+participant scoped hook. LEGACY (a
  // SUCCESSFUL lookup that returned ZERO participant profiles) → the uid compatibility read. Every UNRESOLVED canonical
  // state (loading / error / needs_org / ambiguous) FAILS CLOSED: no legacy uid query, no modern query, its own state.
  const ctx = useStudentContext(studentId);
  const lane = ctx.lane;
  const isModern = lane === 'modern';
  const isLegacy = lane === 'legacy';
  const mv = useStudentModernVideos(isModern ? studentId : '', isModern ? ctx.selectedOrganizationId : '', isModern ? ctx.activeParticipantId : '');

  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  // LEGACY lane fetch — runs ONLY for a confirmed legacy account. One uid read, then PARTITION (§19): A = this legacy
  // org (or all when no legacy org is recorded), B = TENANTLESS legacy uploads (kept accessible in a separate lane so a
  // legacy owner with `users.orgId` never loses them — the §18 bug), C = another org (never shown, never counted).
  const legacyOrgId = userData?.orgId || '';
  const [legacy, setLegacy] = useState({ orgVideos: [], tenantless: [], loading: true, error: false });
  useEffect(() => {
    if (lane !== 'legacy' || !studentId) return undefined; // ONLY a confirmed-legacy account ever hits the uid query
    let alive = true;
    setLegacy({ orgVideos: [], tenantless: [], loading: true, error: false });
    (async () => {
      try {
        const snap = await getDocs(query(collection(db, 'videos'), where('userId', '==', studentId)));
        const all = snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
        const tenantless = all.filter(isTenantlessLegacyVideo);
        const orgVideos = legacyOrgId
          ? all.filter(v => String(v.organizationId || '') === legacyOrgId)                 // chosen legacy org only (excludes tenantless + other-org)
          : all.filter(v => !isTenantlessLegacyVideo(v));                                   // no recorded org → all non-tenantless (back-compat)
        if (alive) setLegacy({ orgVideos, tenantless, loading: false, error: false });
      } catch (_) { if (alive) setLegacy({ orgVideos: [], tenantless: [], loading: false, error: true }); }
    })();
    return () => { alive = false; };
  }, [lane, studentId, legacyOrgId]);

  const showData = isModern || isLegacy;             // the video grid/stats render ONLY in a resolved data lane
  const videos = useMemo(() => (isModern ? mv.videos : (isLegacy ? legacy.orgVideos : [])), [isModern, isLegacy, mv.videos, legacy.orgVideos]);
  const legacyTenantless = isLegacy ? legacy.tenantless : [];
  const loading = lane === 'loading' || (isModern ? mv.loading : (isLegacy ? legacy.loading : false));
  const loadError = lane === 'error' || (isModern ? !!mv.error : (isLegacy ? legacy.error : false));
  const legacyTenantlessCount = legacyTenantless.length;

  // Counts
  const reviewed = videos.filter(v => v.status === 'reviewed').length;
  const pending = videos.filter(v => v.status !== 'reviewed').length;

  // Filtered
  const filtered = useMemo(() => {
    let list = videos;
    if (filter === 'reviewed') list = list.filter(v => v.status === 'reviewed');
    if (filter === 'pending') list = list.filter(v => v.status !== 'reviewed');
    if (search.trim()) list = list.filter(v => (v.videoName || v.title || '').toLowerCase().includes(search.toLowerCase()));
    return list;
  }, [videos, filter, search]);

  return (
    <div style={{ minHeight: '100vh', background: C.bg, color: C.t1, fontFamily: font }}>
      <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0, background: 'radial-gradient(ellipse 60% 40% at 5% 0%,rgba(37,99,235,0.03),transparent 55%),radial-gradient(ellipse 45% 35% at 95% 95%,rgba(240,90,31,0.02),transparent 55%)' }} />

      <StudentNav activeTab="videos" />

      <main style={{ padding: '36px 36px 80px', maxWidth: 1260, margin: '0 auto', position: 'relative', zIndex: 1 }} data-student-mode={lane}>

        <StudentOrgBar ctx={ctx} />

        {lane === 'loading' && (
          <div data-testid="videos-lane-loading" style={{ padding: '60px 0', textAlign: 'center', color: C.t3, fontSize: 13 }}>Resolving your coaching profile…</div>
        )}

        {loadError && (
          <div data-testid="videos-load-error" style={{ background: '#FEF2F2', border: '1px solid #FECACA', color: '#B91C1C', borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: 13 }}>
            We couldn’t load your videos right now. This is a loading problem, not an empty library — please try again.
          </div>
        )}

        {showData && (
        <>
        {legacyTenantlessCount > 0 && (
          <div data-testid="videos-legacy-tenantless" style={{ background: '#F5F4F0', border: '1px solid rgba(0,0,0,0.08)', color: '#6B6B63', borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: 12.5 }}>
            {legacyTenantlessCount} of your videos are legacy uploads with no organisation recorded. They are shown below in a separate section for access but are not part of a coaching organisation.
          </div>
        )}

        {/* HEADER */}
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 24 }}>
          <div>
            <div style={{ fontFamily: fontD, fontSize: 40, letterSpacing: 2, color: C.t1, lineHeight: 1, marginBottom: 5 }}>My Videos</div>
            <div style={{ fontSize: 13, color: C.t3 }}><strong style={{ color: C.t1 }}>{videos.length} videos</strong> · {reviewed} reviewed · {pending} awaiting feedback</div>
          </div>
          <button onClick={() => navigate('/upload')} style={{ background: C.blue, color: 'white', border: 'none', padding: '10px 22px', borderRadius: 10, fontFamily: font, fontSize: 14, fontWeight: 600, cursor: 'pointer', boxShadow: '0 4px 14px rgba(37,99,235,0.20)' }}>⬆ Upload New Video</button>
        </div>

        {/* PROGRESS STRIP — canonical facts + link to the ONE progression surface (no fabricated XP/level) */}
        <div style={{ background: 'linear-gradient(135deg,#EFF6FF,#F0F4FF)', border: '1px solid rgba(37,99,235,0.12)', borderRadius: 14, padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 16, marginBottom: 22, flexWrap: 'wrap' }}>
          <MotusIcon name="target" size={20} color={C.blue} />
          <div><div style={{ fontFamily: fontD, fontSize: 26, color: C.blue, lineHeight: 1 }}>{reviewed}</div><div style={{ fontSize: 12, color: C.t3 }}>Reviewed</div></div>
          <div style={{ width: 1, height: 30, background: C.border }} />
          <div><div style={{ fontFamily: fontD, fontSize: 26, color: C.orange, lineHeight: 1 }}>{pending}</div><div style={{ fontSize: 12, color: C.t3 }}>Awaiting feedback</div></div>
          <div style={{ flex: 1 }} />
          <div style={{ fontSize: 12, color: C.t2 }}>See your focus, level, and next step</div>
          <button onClick={() => navigate(buildMySkillsPath())} style={{ background: 'white', color: C.blue, border: '1px solid rgba(37,99,235,0.25)', padding: '8px 16px', borderRadius: 10, fontFamily: font, fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>View My Progress →</button>
        </div>

        {/* FILTER ROW */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 200, display: 'flex', alignItems: 'center', gap: 10, background: C.card, border: `1px solid ${C.border}`, borderRadius: 11, padding: '10px 16px' }}>
            <MotusIcon name="search" size={16} color={C.t3} />
            <input type="text" placeholder="Search by title, skill, or coach…" value={search} onChange={e => setSearch(e.target.value)}
              style={{ background: 'none', border: 'none', outline: 'none', fontFamily: font, fontSize: 14, color: C.t1, flex: 1 }} />
          </div>
          <FilterPill label={`All (${videos.length})`} active={filter === 'all'} onClick={() => setFilter('all')} color="blue" />
          <FilterPill label={`Reviewed (${reviewed})`} active={filter === 'reviewed'} onClick={() => setFilter('reviewed')} color="green" />
          <FilterPill label={`Pending (${pending})`} active={filter === 'pending'} onClick={() => setFilter('pending')} color="orange" />
        </div>

        {/* VIDEO GRID */}
        {loading ? (
          <div style={{ padding: '60px 0', textAlign: 'center', color: C.t3 }}>Loading videos…</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 18 }}>
            {filtered.map(video => <VideoCard key={video.id} video={video} navigate={navigate} />)}

            {/* UPLOAD CTA */}
            <div onClick={() => navigate('/upload')} style={{ background: C.card, border: `2px dashed rgba(37,99,235,0.20)`, borderRadius: 18, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 280, cursor: 'pointer', padding: 30, textAlign: 'center', gap: 12, transition: 'all .2s' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(37,99,235,0.4)'; e.currentTarget.style.background = 'rgba(37,99,235,0.02)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(37,99,235,0.20)'; e.currentTarget.style.background = C.card; }}>
              <div style={{ width: 52, height: 52, borderRadius: 14, background: C.blueDim, border: '1px solid rgba(37,99,235,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, color: C.blue }}>⬆</div>
              <div style={{ fontFamily: fontD, fontSize: 22, letterSpacing: 1, color: C.t1 }}>Upload Your Next Video</div>
              <div style={{ fontSize: 13, color: C.t3, lineHeight: 1.5 }}>Get AI + coach feedback using the detect → cause → effect → correct → train framework.</div>
              <div style={{ fontSize: 12, color: C.gold, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 5 }}><MotusIcon name="trophy" size={14} color={C.gold} /> Get personal coach feedback on every video</div>
              <button style={{ background: C.blue, color: 'white', border: 'none', padding: '10px 22px', borderRadius: 10, fontFamily: font, fontSize: 13, fontWeight: 600, cursor: 'pointer', boxShadow: '0 4px 14px rgba(37,99,235,0.18)', marginTop: 4 }}>⬆ Upload Video</button>
            </div>
          </div>
        )}

        {/* Pass 85.1A.2 §19 — LEGACY tenantless uploads in an EXPLICIT separate lane (no org attribution, never counted
            in the org stats above, never assigned a participant). Only ever shown for a confirmed legacy account. */}
        {isLegacy && legacyTenantless.length > 0 && (
          <div data-testid="videos-tenantless-section" style={{ marginTop: 32 }}>
            <div style={{ fontFamily: fontD, fontSize: 22, letterSpacing: 1, color: C.t2, marginBottom: 12 }}>Legacy uploads — organisation not assigned</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 18 }}>
              {legacyTenantless.map(video => <VideoCard key={video.id} video={video} navigate={navigate} />)}
            </div>
          </div>
        )}
        </>
        )}
      </main>
    </div>
  );
}

// ── Video Card ──

function VideoCard({ video, navigate }) {
  const title = video.videoName || video.title || 'Untitled Video';
  const sport = video.sport || 'Skiing';
  // Pass 71 — derive the display state from the ONE shared lifecycle helper (was reading a persisted
  // status==='in_review' that no writer ever sets, so "In Review" was dead and claimed videos read "Pending").
  const workflowState = getStudentVideoState(video);
  const isReviewed = workflowState.key === 'reviewed';
  const isInReview = workflowState.key === 'in_review';
  const isPending = !isReviewed && !isInReview;
  const date = video.createdAt?.seconds ? new Date(video.createdAt.seconds * 1000).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
  const coach = video.reviewedByName || video.assignedToName || '';
  const coachInitials = coach ? coach.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) : '';
  const sportLabel = sport.toLowerCase().includes('snowboard') ? 'Snowboard' : 'Skiing';
  const videoUrl = video.videoURL || video.videoUrl || '';

  // Badge colors for light theme
  const badgeStyle = isReviewed
    ? { bg: 'rgba(34,197,94,0.08)', border: 'rgba(34,197,94,0.20)', color: C.green, label: 'Reviewed' }
    : isInReview
      ? { bg: 'rgba(37,99,235,0.08)', border: 'rgba(37,99,235,0.20)', color: C.blue, label: 'In Review' }
      : { bg: 'rgba(240,90,31,0.08)', border: 'rgba(240,90,31,0.20)', color: C.orange, label: 'Pending' };

  return (
    <div onClick={() => navigate(`/reviews/${video.id}`)} style={{ background: C.card, border: `1px solid ${isPending ? 'rgba(240,90,31,0.12)' : C.border}`, borderRadius: 18, overflow: 'hidden', cursor: 'pointer', transition: 'all .2s' }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = C.borderH; e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 16px 40px rgba(0,0,0,0.06)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = isPending ? 'rgba(240,90,31,0.12)' : C.border; e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}>

      {/* Thumbnail */}
      <div style={{ position: 'relative', height: 170, background: C.card2, overflow: 'hidden' }}>
        {videoUrl ? (
          <video
            src={videoUrl}
            preload="metadata"
            muted
            playsInline
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            onLoadedData={e => { e.target.currentTime = 1; }}
          />
        ) : (
          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: `linear-gradient(135deg, ${C.card2}, ${C.card3})` }}>
            <MotusIcon name={sportLabel === 'Snowboard' ? 'snowboarder' : 'skier'} size={80} color={C.t3} strokeWidth={1.2} style={{ opacity: 0.35 }} />
          </div>
        )}
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top,rgba(255,255,255,0.85) 0%,transparent 50%)' }} />
        {/* Play overlay on hover */}
        <div className="play-overlay" style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 44, height: 44, background: 'rgba(0,0,0,0.3)', backdropFilter: 'blur(6px)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color: 'white', opacity: 0, transition: 'opacity .2s' }} onMouseEnter={e => e.currentTarget.style.opacity = 1} onMouseLeave={e => e.currentTarget.style.opacity = 0}>▶</div>
        {/* Status badge */}
        <div style={{ position: 'absolute', top: 10, left: 10, padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 5, background: badgeStyle.bg, border: `1px solid ${badgeStyle.border}`, color: badgeStyle.color }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'currentColor' }} />
          {badgeStyle.label}
        </div>
        {/* XP badge */}
        {isReviewed && <div style={{ position: 'absolute', top: 10, right: 10, background: C.goldDim, border: '1px solid rgba(217,119,6,0.20)', color: C.gold, fontSize: 10, fontWeight: 800, padding: '3px 8px', borderRadius: 20 }}>Reviewed</div>}
      </div>

      {/* Body */}
      <div style={{ padding: '16px 18px 18px' }}>
        <div style={{ fontFamily: fontD, fontSize: 20, letterSpacing: 1, color: C.t1, marginBottom: 5, lineHeight: 1.1 }}>{title}</div>
        <div style={{ fontSize: 12, color: C.t3, marginBottom: 13, display: 'flex', alignItems: 'center', gap: 7 }}>
          {date} <span style={{ color: C.borderH }}>·</span> {sport}
        </div>

        {/* Coach row */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 13 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 28, height: 28, borderRadius: '50%', background: coach ? `linear-gradient(135deg,${C.blue},${C.purple})` : C.card2, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: coach ? 'white' : C.t3 }}>
              {coachInitials || '?'}
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: C.t1 }}>{coach || 'Awaiting Instructor'}</div>
              <div style={{ fontSize: 11, color: C.t3 }}>{coach ? 'Instructor' : 'Being assigned'}</div>
            </div>
          </div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: badgeStyle.bg, border: `1px solid ${badgeStyle.border}`, color: badgeStyle.color }}>
            {isReviewed ? '✓ Reviewed' : isPending ? <><MotusIcon name="clock" size={12} color={C.orange} /> Pending</> : '● In Review'}
          </div>
        </div>

        {/* Footer */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 13, borderTop: `1px solid ${C.border}` }}>
          <div style={{ fontSize: 12, color: C.t3, display: 'flex', alignItems: 'center', gap: 5 }}>
            {isReviewed ? <><MotusIcon name="message" size={13} color={C.t3} /> Feedback ready</> : <><MotusIcon name="clock" size={13} color={C.t3} /> Feedback coming soon</>}
          </div>
          {isReviewed ? (
            <button style={{ background: C.blue, color: 'white', border: 'none', padding: '7px 14px', borderRadius: 8, fontFamily: font, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>View Feedback →</button>
          ) : (
            <div style={{ background: C.card2, color: C.t3, border: `1px solid ${C.border}`, padding: '7px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600 }}>Awaiting Review</div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Filter Pill ──

function FilterPill({ label, active, onClick, color }) {
  const colors = {
    blue: { activeBg: C.blue, activeColor: 'white' },
    green: { activeBg: C.greenDim, activeColor: C.green, activeBorder: 'rgba(34,197,94,0.20)' },
    orange: { activeBg: C.orangeDim, activeColor: C.orange, activeBorder: 'rgba(240,90,31,0.20)' },
  }[color] || { activeBg: C.blue, activeColor: 'white' };

  return (
    <button onClick={onClick} style={{
      padding: '8px 16px', borderRadius: 22, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: font, whiteSpace: 'nowrap', transition: 'all .18s',
      background: active ? colors.activeBg : C.card,
      border: `1px solid ${active ? (colors.activeBorder || colors.activeBg) : C.border}`,
      color: active ? colors.activeColor : C.t3,
    }}>{label}</button>
  );
}

export default StudentVideosPage;
